<template>
  <div
    class="carousel-arrows"
    :class="[
      typeClasses.container,
      {
        'carousel-arrows--large': large,
        'carousel-arrows--outlined': outlined,
      },
    ]"
  >
    <button
      class="
        carousel-arrows__arrow
        carousel-arrows__arrow--previous
      "
      :class="typeClasses.previous"
      :disabled="previousDisabled"
      type="button"
      @click="handleClick('previous')"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.carousel.previous')"
      />

      <icon-carousel-arrow />
    </button>

    <button
      class="
        carousel-arrows__arrow
        carousel-arrows__arrow--next
      "
      :class="typeClasses.next"
      :disabled="nextDisabled"
      type="button"
      @click="handleClick('next')"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.carousel.next')"
      />

      <icon-carousel-arrow />
    </button>
  </div>
</template>

<script>

/**
 * Vue: Carousel arrows (carousel-arrows)
 * -----------------------------------------------------------------------------
 * Generic carousel arrows component.
 *
 * @param {Boolean} [large] - Larger margins from the edge.
 * @param {Boolean} [nextDisabled] - Next disabled, for custom carousel.
 * @param {Boolean} [outlined] - Outline arrow circles.
 * @param {Boolean} [previousDisabled] - Previous disabled, for custom carousel.
 * @param {String} [type] - Type of carousel, 'custom', 'swiper' or 'splide'.
 *
 * @emits next - Next button click.
 * @emits previous - Previous button click.
 *
 */
import IconCarouselArrow from '~icons/directional-navigation/carousel-arrow.svg'

export default {
  name: 'CarouselArrows',

  components: {
    IconCarouselArrow,
  },

  props: {
    large: {
      type: Boolean,
      default: false,
    },
    nextDisabled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    previousDisabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
      required: true,
    },
  },

  emits: {
    next: () => true,
    previous: () => true,
  },

  computed: {

    /**
     * Compute type-based classes.
     * @returns {String}
     */
    typeClasses() {
      switch (this.type) {
        case 'splide':
          return {
            container: 'splide__arrows',
            next: 'splide__arrow splide__arrow--next',
            previous: 'splide__arrow splide__arrow--prev',
          }

        case 'swiper':
          return {
            next: 'swiper-button swiper-button-next',
            previous: 'swiper-button swiper-button-prev',
          }

        default:
          return {
            container: '',
            next: '',
            previous: '',
          }
      }
    },
  },

  methods: {

    /**
     * Handle button click.
     * @param {String} type - Type of button.
     */
    handleClick(type) {
      if (this.swiper) {
        return
      }

      this.$emit(type)
    },
  },
}
</script>

<style lang="scss">
@import './carousel-arrows';
</style>
